import React from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';

import fetchDailySummary from './api/dailySummaryApi.js';
import DailySummaryTable from './components/DailySummaryTable.jsx';

function formatDate(value) {
  if (!value) {
    return '';
  }

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return `${date.getMonth() + 1}/${String(date.getDate()).padStart(2, '0')}/${String(
    date.getFullYear()
  ).slice(-2)}`;
}

function formatMoney(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '';
  }

  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

function formatInteger(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '0';
  }

  return number.toLocaleString('en-US');
}

function DailySummaryPage() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [allOrders, setAllOrders] = React.useState([]);
  const [positiveOrders, setPositiveOrders] = React.useState([]);

  React.useEffect(() => {
    let active = true;

    async function loadDailySummary() {
      setLoading(true);
      setError('');

      try {
        const result = await fetchDailySummary();

        if (!active) {
          return;
        }

        setAllOrders(Array.isArray(result?.allOrders) ? result.allOrders : []);
        setPositiveOrders(Array.isArray(result?.positiveOrders) ? result.positiveOrders : []);
      } catch (fetchError) {
        if (!active) {
          return;
        }

        setAllOrders([]);
        setPositiveOrders([]);
        setError(
          fetchError?.response?.data?.error || fetchError?.message || 'Failed to load daily summary'
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadDailySummary();

    return () => {
      active = false;
    };
  }, []);

  const allOrdersColumns = React.useMemo(
    () => [
      { key: 'OrderDate', label: 'Order Date', render: formatDate },
      { key: 'Orders', label: 'Orders', align: 'right', render: formatInteger },
      { key: 'OrderTotal', label: 'Order Total', align: 'right', render: formatMoney },
    ],
    []
  );

  const positiveOrdersColumns = React.useMemo(
    () => [
      { key: 'OrderDate', label: 'Order Date', render: formatDate },
      { key: 'Orders', label: 'Orders', align: 'right', render: formatInteger },
      {
        key: 'MedianOrder',
        label: 'Median Order',
        align: 'right',
        render: (value) =>
          value === null || value === undefined || value === '' ? 'Pending' : formatMoney(value),
      },
    ],
    []
  );

  return (
    <Box sx={{ py: 1 }}>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3} alignItems="flex-start">
          <DailySummaryTable title="All Orders" rows={allOrders} columns={allOrdersColumns} />
          <DailySummaryTable
            title="All Orders — Zero Dollar Orders Excluded"
            rows={positiveOrders}
            columns={positiveOrdersColumns}
          />
        </Stack>
      )}
    </Box>
  );
}

export default React.memo(DailySummaryPage);
